import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCreateBrandStory, useUpdateBrandStory, BrandStory } from '@/hooks/useBrandStories';

const brandStorySchema = z.object({
  title: z.string().min(1, 'Title is required').max(60, 'Title must be 60 characters or less'),
  description: z.string().max(200, 'Description must be 200 characters or less').optional(),
  author: z.string().min(1, 'Author is required').max(50, 'Author name must be 50 characters or less'),
  role: z.string().min(1, 'Role is required').max(50, 'Role must be 50 characters or less'),
  image_url: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  background_image_url: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  is_active: z.boolean(),
  sort_order: z.number().min(0, 'Sort order must be 0 or greater'),
});

type BrandStoryFormData = z.infer<typeof brandStorySchema>;

interface BrandStoryFormProps {
  story?: BrandStory | null;
  onClose: () => void;
}

export const BrandStoryForm: React.FC<BrandStoryFormProps> = ({ story, onClose }) => {
  const [titleLength, setTitleLength] = useState(0);
  const [descriptionLength, setDescriptionLength] = useState(0);
  const [authorLength, setAuthorLength] = useState(0);
  const [roleLength, setRoleLength] = useState(0);

  const createMutation = useCreateBrandStory();
  const updateMutation = useUpdateBrandStory();

  const form = useForm<BrandStoryFormData>({
    resolver: zodResolver(brandStorySchema),
    defaultValues: {
      title: '',
      description: '',
      author: '',
      role: '',
      image_url: '',
      background_image_url: '',
      is_active: true,
      sort_order: 0,
    },
  });

  useEffect(() => {
    if (story) {
      form.reset({
        title: story.title,
        description: story.description || '',
        author: story.author,
        role: story.role,
        image_url: story.image_url || '',
        background_image_url: story.background_image_url || '',
        is_active: story.is_active,
        sort_order: story.sort_order,
      });
      setTitleLength(story.title.length);
      setDescriptionLength(story.description?.length || 0);
      setAuthorLength(story.author.length);
      setRoleLength(story.role.length);
    }
  }, [story, form]);

  const onSubmit = (data: BrandStoryFormData) => {
    if (story) {
      updateMutation.mutate(
        { id: story.id, data: data as any },
        {
          onSuccess: onClose,
        }
      );
    } else {
      createMutation.mutate(data as any, {
        onSuccess: onClose,
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {story ? 'Edit Brand Story' : 'Create Brand Story'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter story title"
                      onChange={(e) => {
                        field.onChange(e);
                        setTitleLength(e.target.value.length);
                      }}
                    />
                  </FormControl>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <FormMessage />
                    <span>{titleLength}/60</span>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter story description"
                      className="min-h-[100px]"
                      onChange={(e) => {
                        field.onChange(e);
                        setDescriptionLength(e.target.value.length);
                      }}
                    />
                  </FormControl>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <FormMessage />
                    <span>{descriptionLength}/200</span>
                  </div>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="author"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Author name"
                        onChange={(e) => {
                          field.onChange(e);
                          setAuthorLength(e.target.value.length);
                        }}
                      />
                    </FormControl>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <FormMessage />
                      <span>{authorLength}/50</span>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Author role"
                        onChange={(e) => {
                          field.onChange(e);
                          setRoleLength(e.target.value.length);
                        }}
                      />
                    </FormControl>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <FormMessage />
                      <span>{roleLength}/50</span>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Author Image URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://example.com/image.jpg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="background_image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Background Image URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://example.com/background.jpg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={0}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Show this story on the website
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : story ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};